import { createLogger } from './logger';
import { supabase } from './supabase';

const logger = createLogger({ module: 'featureFlags' });

export const FILE_SERVICE_FLAGS = {
  USE_MODERN_SERVICE: 'use_modern_file_service',
  ENABLE_QUOTA: 'enable_storage_quota',
  ENABLE_THUMBNAILS: 'enable_thumbnail_generation',
  ENABLE_GOOGLE_DRIVE: 'enable_google_drive_integration',
  ENABLE_LINKEDIN: 'enable_linkedin_integration'
} as const;

export const RETROSPECTIVE_FLAGS = {
  ENABLE_FEATURE: 'enable_retrospectives',
  ENABLE_AI_ANALYSIS: 'enable_retro_ai_analysis',
  ENABLE_CARD_GENERATION: 'enable_retro_card_generation',
  ENABLE_CONTENT_LINKING: 'enable_retro_content_linking'
} as const;

export const NOTIFICATION_FLAGS = {
  INFRASTRUCTURE_ENABLED: 'notification_infrastructure_enabled',
  BELL_NOTIFICATIONS_ENABLED: 'bell_notifications_enabled',
  BANNER_NOTIFICATIONS_ENABLED: 'banner_notifications_enabled',
  NOTIFICATION_SYSTEM_COMPLETE: 'notification_system_complete'
} as const;

export const DEBUG_FLAGS = {
  SHOW_CACHE_DEBUG: 'show_cache_debug'
} as const;

// Default values for feature flags
const DEFAULT_FLAGS = {
  [FILE_SERVICE_FLAGS.USE_MODERN_SERVICE]: false,  // Set to false to respect database value
  [FILE_SERVICE_FLAGS.ENABLE_QUOTA]: false,
  [FILE_SERVICE_FLAGS.ENABLE_THUMBNAILS]: false,
  [RETROSPECTIVE_FLAGS.ENABLE_FEATURE]: false,
  [RETROSPECTIVE_FLAGS.ENABLE_AI_ANALYSIS]: false,
  [RETROSPECTIVE_FLAGS.ENABLE_CARD_GENERATION]: false,
  [RETROSPECTIVE_FLAGS.ENABLE_CONTENT_LINKING]: false,
  [NOTIFICATION_FLAGS.INFRASTRUCTURE_ENABLED]: true,  // Enable Phase 4A by default
  [NOTIFICATION_FLAGS.BELL_NOTIFICATIONS_ENABLED]: true,  // Enable Phase 4B by default
  [NOTIFICATION_FLAGS.BANNER_NOTIFICATIONS_ENABLED]: true,  // Enable banner notifications by default
  [NOTIFICATION_FLAGS.NOTIFICATION_SYSTEM_COMPLETE]: false
} as const;

export type FileServiceFlag = keyof typeof FILE_SERVICE_FLAGS;

export interface FeatureFlagRecord {
  id: string;
  flag: string;
  enabled: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export class FeatureFlags {
  private static cache: Map<string, boolean> = new Map();
  private static cacheExpiry: Map<string, number> = new Map();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  static async isEnabled(flagName: string): Promise<boolean> {
    // Special case for 'dummy' flags - always return true
    if (flagName === 'dummy') {
      logger.debug(`Using default true for dummy feature flag`);
      return true;
    }
    
    // Check cache first
    const now = Date.now();
    const cachedValue = this.cache.get(flagName);
    const cacheExpiry = this.cacheExpiry.get(flagName);

    if (cachedValue !== undefined && cacheExpiry && now < cacheExpiry) {
      return cachedValue;
    }

    try {
      // For now, use default values since we can't access Statsig client from a non-React context
      // This is a temporary fix - feature flags should be checked in React components using hooks
      logger.debug('Using default value for feature flag (Statsig not available in utility context)', { flagName });
      const defaultValue = DEFAULT_FLAGS[flagName as keyof typeof DEFAULT_FLAGS] ?? false;
      this.cache.set(flagName, defaultValue);
      this.cacheExpiry.set(flagName, now + this.CACHE_TTL);
      return defaultValue;
    } catch (error) {
      logger.error('Error checking Statsig feature flag', { flagName, error });
      const defaultValue = DEFAULT_FLAGS[flagName as keyof typeof DEFAULT_FLAGS] ?? false;
      this.cache.set(flagName, defaultValue);
      this.cacheExpiry.set(flagName, now + this.CACHE_TTL);
      return defaultValue;
    }
  }

  static clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  public static async setEnabled(flag: string, enabled: boolean, metadata?: Record<string, unknown>): Promise<void> {
    // Skip API calls in development mode
    if (import.meta.env.DEV) {
      logger.debug(`[DEV] Simulating feature flag change: ${flag} = ${enabled}`);
      this.cache.set(flag, enabled);
      return;
    }

    try {
      const { error } = await supabase
        .from('feature_flags')
        .update({ enabled, metadata: metadata ?? {} })
        .eq('flag', flag);

      if (error) {
        logger.error('Error updating feature flag:', { flag, enabled, error });
        throw error;
      }

      // Update cache
      this.cache.set(flag, enabled);

      // Log event
      await this.logEvent(flag, enabled, metadata);
    } catch (error) {
      logger.error('Unexpected error setting feature flag:', { flag, enabled, error });
      throw error;
    }
  }

  private static async logEvent(flag: string, enabled: boolean, metadata?: Record<string, unknown>): Promise<void> {
    // Skip API calls in development mode
    if (import.meta.env.DEV) {
      logger.debug(`[DEV] Simulating feature flag event logging: ${flag} = ${enabled}`);
      return;
    }

    try {
      const { error } = await supabase
        .from('feature_flag_events')
        .insert([{ flag, enabled, metadata }]);

      if (error) {
        logger.error('Error logging feature flag event:', { flag, enabled, error });
      }
    } catch (error) {
      logger.error('Unexpected error logging feature flag event:', { flag, enabled, error });
    }
  }

  public static async getMetadata(flag: string): Promise<Record<string, unknown> | null> {
    // Skip API calls in development mode
    if (import.meta.env.DEV) {
      logger.debug(`[DEV] Simulating feature flag metadata retrieval for: ${flag}`);
      return {};
    }

    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('metadata')
        .eq('flag', flag)
        .single();

      if (error) {
        logger.error('Error fetching feature flag metadata:', { flag, error });
        return null;
      }

      return data?.metadata ?? null;
    } catch (error) {
      logger.error('Unexpected error getting feature flag metadata:', { flag, error });
      return null;
    }
  }
} 
